import React from 'react';
import { Page, View, Text, StyleSheet } from '@react-pdf/renderer';
import { ReportData } from '../../types/report.types';
import { useSectionPages } from '../SectionPageContext';
import { getGlobalAbbreviations, normalizeSeverity } from '../../utils/findingsUtils';

interface KeyFindingsListPageProps {
  reportData: ReportData;
  sectionId?: string;
}

const styles = StyleSheet.create({
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 0,
    right: 0,
    fontSize: 10,
    color: 'grey',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 24,
  },
  footerLeft: {
    textAlign: 'left',
    flex: 1,
  },
  footerRight: {
    textAlign: 'right',
    flex: 1,
  },
});

const KeyFindingsListPage: React.FC<KeyFindingsListPageProps> = ({ reportData, sectionId }) => {
  const { updateSectionPage } = useSectionPages();

  // Helper function to format status display
  const formatStatus = (status: string) => {
    if (!status) return 'Unknown';

    // Convert status to display format
    const statusLower = status.toLowerCase().trim();

    if (statusLower === 'business accepts risk' || statusLower === 'business accept risk') {
      return 'Accepts Risk';
    }
    if (statusLower === 'business non actionable issue' || statusLower === 'business non-actionable issue' || statusLower === 'business non actionable') {
      return 'Non-Actionable';
    }

    // Return original status with proper capitalization
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  };

  // Helper function to get status colors
  const getStatusColors = (status: string) => {
    const statusLower = status.toLowerCase().trim();

    if (statusLower === 'open') {
      return { bg: '#fee2e2', text: '#dc2626' };
    }
    if (statusLower === 'closed') {
      return { bg: '#dcfce7', text: '#16a34a' };
    }
    if (statusLower === 'business accepts risk' || statusLower === 'business accept risk') {
      return { bg: '#fef3c7', text: '#f59e0b' };
    }
    if (statusLower === 'business non actionable issue' || statusLower === 'business non-actionable issue' || statusLower === 'business non actionable') {
      return { bg: '#f3f4f6', text: '#6b7280' };
    }

    return { bg: '#f3f4f6', text: '#374151' };
  };

  return (
    <Page size="A4" id={sectionId} style={{
      flexDirection: 'column',
      backgroundColor: '#ffffff',
      padding: '20mm 15mm',
      fontFamily: 'Helvetica',
      fontSize: 12,
    }}>
      <View style={{ flex: 1 }}>
        <Text style={{ fontSize: 16, fontWeight: 'bold', color: '#2563eb', marginBottom: 16, lineHeight: 1.4 }}>
          KEY FINDINGS
        </Text>
        <Text style={{ fontSize: 12, marginBottom: 16, color: '#374151', lineHeight: 1.4 }}>
          The table below contains a list of the vulnerabilities identified during this assessment:
        </Text>
        <View style={{
          borderRadius: 6,
          borderWidth: 1,
          borderColor: '#cbd5e1',
          overflow: 'hidden',
          marginBottom: 16,
          backgroundColor: '#fff',
          width: '100%',
          alignSelf: 'center',
        }}>
          <View style={{ flexDirection: 'column', width: '100%' }}>
            {/* Table Header - Matching Scope Table Style */}
            <View style={{
              flexDirection: 'row',
              backgroundColor: '#1e40af',
              borderBottomWidth: 2,
              borderBottomColor: '#1e3a8a'
            }}>
              <Text style={{ paddingVertical: 8, paddingHorizontal: 10, fontWeight: 'bold', width: '8%', textAlign: 'center', color: '#fff', fontSize: 10, lineHeight: 1.3 }}>No.</Text>
              <Text style={{ paddingVertical: 8, paddingHorizontal: 10, fontWeight: 'bold', width: '12%', color: '#fff', fontSize: 10, lineHeight: 1.3 }}>ID</Text>
              <Text style={{ paddingVertical: 8, paddingHorizontal: 10, fontWeight: 'bold', width: '40%', color: '#fff', fontSize: 10, lineHeight: 1.3 }}>Finding Title</Text>
              <Text style={{ paddingVertical: 8, paddingHorizontal: 10, fontWeight: 'bold', width: '20%', textAlign: 'center', color: '#fff', fontSize: 10, lineHeight: 1.3 }}>Severity</Text>
              <Text style={{ paddingVertical: 8, paddingHorizontal: 10, fontWeight: 'bold', width: '20%', textAlign: 'center', color: '#fff', fontSize: 10, lineHeight: 1.3 }}>Status</Text>
            </View>
            {/* Table Rows - Improved UI */}
            {(() => {
              const abbrMap = getGlobalAbbreviations(reportData);
              const reportsList = reportData.reports_list || [];
              const config = reportData.finding_config;

              const sevOrder: Record<string, number> = {
                critical: 1,
                high: 2,
                medium: 3,
                low: 4,
                info: 5,
                none: 5
              };

              return [...reportsList]
                .sort((a, b) => {
                  const aSev = normalizeSeverity(a.severity_category).toLowerCase();
                  const bSev = normalizeSeverity(b.severity_category).toLowerCase();
                  if (sevOrder[aSev] !== sevOrder[bSev]) {
                    return (sevOrder[aSev] || 99) - (sevOrder[bSev] || 99);
                  }
                  
                  // Use submitted_date as secondary sort
                  const dateA = new Date((a as any).submitted_date || 0).getTime();
                  const dateB = new Date((b as any).submitted_date || 0).getTime();
                  if (dateA !== dateB) return dateA - dateB;

                  // Use report_id as absolute tie-breaker
                  return ((a as any).report_id || 0) - ((b as any).report_id || 0);
                })
                .map((finding, idx) => {
                  const titleKey = (finding.title || '').toLowerCase().trim();
                  const abbr = abbrMap[titleKey] || finding.abbreviation || '??';

                  // Get status colors
                  const statusColors = getStatusColors(finding.status || '');
                  const displayStatus = formatStatus(finding.status || '');

                  return (
                    <View key={idx} style={{
                      flexDirection: 'row',
                      backgroundColor: idx % 2 === 0 ? '#f8fafc' : '#fff',
                      borderBottomWidth: idx === (reportData.reports_list?.length ?? 0) - 1 ? 0 : 1,
                      borderBottomColor: '#e2e8f0',
                      minHeight: 28,
                      alignItems: 'center',
                    }}>
                      <Text style={{
                        paddingVertical: 6,
                        paddingHorizontal: 10,
                        width: '8%',
                        textAlign: 'center',
                        fontWeight: 'bold',
                        color: '#475569',
                        fontSize: 10,
                        lineHeight: 1.3
                      }}>{idx + 1}</Text>
                      <Text style={{
                        paddingVertical: 6,
                        paddingHorizontal: 10,
                        width: '12%',
                        fontWeight: 'bold',
                        color: '#1e40af',
                        fontSize: 10,
                        lineHeight: 1.3
                      }}>{abbr}</Text>
                      <Text style={{
                        paddingVertical: 6,
                        paddingHorizontal: 10,
                        width: '40%',
                        fontSize: 10,
                        color: '#1e293b',
                        lineHeight: 1.3
                      }}>{finding.title}</Text>
                      <View style={{ paddingVertical: 6, paddingHorizontal: 10, width: '20%', alignItems: 'center' }}>
                        {(() => {
                          let sev = (finding.severity_category || 'info').toLowerCase().trim();
                          if (!sev || sev === 'none') sev = 'info';

                          let bgColor = '#64748b';
                          if (sev === 'critical') {
                            bgColor = '#dc2626';
                          } else if (sev === 'high') {
                            bgColor = '#ea580c';
                          } else if (sev === 'medium') {
                            bgColor = '#eab308';
                          } else if (sev === 'low') {
                            bgColor = '#16a34a';
                          } else if (sev === 'info') {
                            bgColor = '#64748b';
                          }
                          return (
                            <View style={{
                              backgroundColor: bgColor,
                              borderRadius: 8,
                              paddingHorizontal: 8,
                              paddingVertical: 3,
                              flexDirection: 'row',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}>
                              <Text style={{
                                fontWeight: 'bold',
                                color: '#fff',
                                fontSize: 9,
                                textTransform: 'capitalize',
                                lineHeight: 1.3,
                              }}>{sev}</Text>
                            </View>
                          );
                        })()}
                      </View>
                      <View style={{ paddingVertical: 6, paddingHorizontal: 10, width: '20%', alignItems: 'center' }}>
                        <View style={{
                          backgroundColor: statusColors.bg,
                          borderRadius: 8,
                          paddingHorizontal: 8,
                          paddingVertical: 3,
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}>
                          <Text style={{
                            color: statusColors.text,
                            fontWeight: 'bold',
                            fontSize: 9,
                            lineHeight: 1.3,
                          }}>{displayStatus}</Text>
                        </View>
                      </View>
                    </View>
                  );
                });
            })()}
          </View>
        </View>
        <Text style={{ fontSize: 10, color: '#64748b', textAlign: 'center', marginTop: 4, fontStyle: 'italic', lineHeight: 1.3 }}>
          Table: Summary of Key Findings
        </Text>
      </View>
      <View style={styles.footer} fixed>
        <Text style={styles.footerLeft}>{reportData.document_number || 'Document Number'}</Text>
        <Text style={styles.footerRight} render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} />
      </View>
      <Text style={{ display: 'none' }} render={({ pageNumber }) => { updateSectionPage('KeyFindingsList', pageNumber); return ''; }} fixed />
    </Page>
  );
};

export default KeyFindingsListPage; 